// GtSequences (C1-SEQ2) — the Sequences screen.
//
// /sequences          every run: who it was for, where it got to, who replied.
// /sequences/:runId   one run. People on the left, the selected person's whole
//                     sequence on the right. Read, edit, rewrite, approve. Once
//                     approved, follow where each person is and pause, skip,
//                     send now or stop.
//
// Built for 500 to 5,000 people: counts first, search, filters, 200 at a time.
// Nothing sends until a person is approved. Sending runs on the server in the
// sending window; this screen only shows it.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE from '../../config';
import GtLoader from '../../components/GtLoader';
import GtEditor from '../../components/GtEditor';
import { gtConfirm } from '../../components/GtConfirm';
import { planSummary } from '../../components/GtSequenceStart';

const plural = (n, one, many) => (n || 0) + ' ' + (n === 1 ? one : many);
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
function when(d, withTime) {
  if (!d) return '';
  const x = new Date(d);
  return x.toLocaleString('en-GB', withTime === false ? { day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const DAYNAME = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
function windowText(w) {
  if (!w) return '';
  const d = (w.days || []).slice().sort();
  const run = d.length > 1 && d.every((x, i) => i === 0 || x === d[i - 1] + 1);
  const days = d.length === 7 ? 'Every day' : run ? DAYNAME[d[0]] + ' to ' + DAYNAME[d[d.length - 1]] : d.map((x) => DAYNAME[x].slice(0, 3)).join(', ');
  return days + ', ' + w.start + ' to ' + w.end + (w.tz && w.tz !== 'Europe/London' ? ' (' + w.tz + ')' : ' UK time');
}

// C1-SEQ6: a real LinkedIn link, or null. A full linkedin.com address, or a
// bare profile handle ("jane-smith-1a2b"). Anything else (a name typed into
// the field, say) is not a link.
function linkedinHref(v) {
  const s = String(v || '').trim();
  if (!s) return null;
  if (/^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/\S+$/i.test(s)) return /^https?:\/\//i.test(s) ? s : 'https://' + s;
  if (/^[A-Za-z0-9][A-Za-z0-9_%-]{2,99}$/.test(s)) return 'https://www.linkedin.com/in/' + s;
  return null;
}

const RUN_STATUS = {
  writing: ['Writing', 'blue'], review: ['Waiting for approval', 'amber'], sending: ['Sending', 'teal'],
  stopped: ['Stopped', 'slate'], finished: ['Finished', 'green'],
};
const PERSON = {
  queued: ['Waiting to be written', 'slate'], writing: ['Being written', 'blue'], ready: ['Ready to approve', 'amber'],
  failed: ['Writing failed', 'rose'], approved: ['Approved', 'teal'], sending: ['In sequence', 'teal'], paused: ['Paused', 'slate'],
  replied: ['Replied', 'green'], bounced: ['Bounced', 'rose'], unsubscribed: ['Unsubscribed', 'rose'], stopped: ['Stopped', 'slate'],
  finished: ['Finished, no reply', 'slate'],
};
function personLabel(p) {
  const n = p.step_count || (p.steps ? p.steps.length : 0);
  if (p.status === 'approved') return 'Approved, email 1 goes next';
  if (p.status === 'sending') return 'Email ' + p.current_step + ' of ' + n + ' sent';
  if (p.status === 'paused') return 'Paused after email ' + p.current_step + ' of ' + n;
  if (p.status === 'replied') return 'Replied' + (p.replied_after_step ? ' after email ' + p.replied_after_step : '');
  if (p.status === 'stopped' && p.stop_reason) return p.stop_reason;
  return (PERSON[p.status] || [p.status])[0];
}
function Pill({ status, map, label }) {
  const m = (map || PERSON)[status] || [status, 'slate'];
  return <span className={'gtsq-pill gtsq-pill--' + m[1]}>{label || m[0]}</span>;
}

export default function GtSequences({ getToken }) {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const headers = { ...(opts.headers || {}), Authorization: 'Bearer ' + token };
    if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    const res = await fetch(API_BASE + path, { ...opts, headers });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(body.message || body.error || ('request failed: ' + res.status)); err.status = res.status; throw err; }
    return body;
  }, [getToken]);
  const seq = useRef(0);
  const flash = useCallback((msg, isErr) => {
    seq.current += 1; const n = seq.current;
    setToast({ msg, err: !!isErr });
    setTimeout(() => { if (seq.current === n) setToast(null); }, isErr ? 7000 : 3500);
  }, []);
  return (
    <>
      {runId ? <RunView runId={runId} authFetch={authFetch} flash={flash} navigate={navigate} getToken={getToken} /> /* C1-SEQ8 */
        : <RunList authFetch={authFetch} flash={flash} navigate={navigate} />}
      {toast && <div className={'gtc-toast' + (toast.err ? ' gtc-toast--err' : '')} role="status"><span>{toast.msg}</span></div>}
    </>
  );
}

// ── Every run ─────────────────────────────────────────────────────────────
function RunList({ authFetch, flash, navigate }) {
  const [runs, setRuns] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  useEffect(() => {
    authFetch('/api/sequences/runs').then((r) => setRuns(r.runs || [])).catch((e) => { setRuns([]); flash(e.message, true); });
  }, [authFetch, flash]);
  const ql = q.trim().toLowerCase();
  const shown = (runs || []).filter((r) => (status === 'all' || r.status === status)
    && (!ql || (r.name + ' ' + r.role_title + ' ' + (r.source_label || '') + ' ' + (r.created_by_name || '')).toLowerCase().indexOf(ql) !== -1));
  const counts = {};
  (runs || []).forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
  return (
    <div className="gtc-wrap gtsq">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Sequences</h1>
          <p className="gtc-sub">Personal emails about a role, written for each person, approved by you, sent in the same thread until they reply.</p>
        </div>
      </div>
      {runs === null ? <GtLoader compact /> : runs.length === 0 ? (
        <div className="gtsb-empty">
          <div className="gtsb-empty-mark">◆</div>
          <h3>No sequences yet</h3>
          <p>Tick people on a role's pipeline or on a bench and choose Start sequence. Every step is written for each person and waits here for your approval.</p>
        </div>
      ) : (
        <>
          <div className="gtsq-listbar">
            <input className="gtsb-search" placeholder="Search by role, run or who started it…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="gtpl-chips">
              <button type="button" className={'gtsb-chip' + (status === 'all' ? ' gtsb-chip--on' : '')} onClick={() => setStatus('all')}>All {runs.length}</button>
              {Object.keys(RUN_STATUS).filter((k) => counts[k]).map((k) => (
                <button type="button" key={k} className={'gtsb-chip' + (status === k ? ' gtsb-chip--on' : '')} onClick={() => setStatus(k)}>{RUN_STATUS[k][0]} <b>{counts[k]}</b></button>
              ))}
            </div>
          </div>
          <div className="gtsq-runs">
            {shown.length === 0 && <div className="gtr-emptytab">No runs match.</div>}
            {shown.map((r) => (
              <button type="button" key={r.id} className="gtsq-run" onClick={() => navigate('/sequences/' + r.id)}>
                <div className="gtsq-run-main">
                  <div className="gtsq-run-title"><b>{r.role_title}</b><Pill status={r.status} map={RUN_STATUS} /></div>
                  <div className="gtsq-run-sub">
                    {(r.plan && r.plan.name) || r.name}{r.source_label ? ' · from ' + r.source_label : ''} · started {when(r.created_at, false)}{r.created_by_name ? ' by ' + r.created_by_name : ''}
                  </div>
                </div>
                <div className="gtsq-run-stats">
                  <span><b>{r.total}</b>people</span>
                  {r.ready > 0 && <span className="gtsq-stat--amber"><b>{r.ready}</b>to approve</span>}
                  <span><b>{r.reached}</b>reached</span>
                  <span className={r.replied ? 'gtsq-stat--green' : ''}><b>{r.replied}</b>replied{r.reached ? ' (' + pct(r.replied, r.reached) + '%)' : ''}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── One run ───────────────────────────────────────────────────────────────
const FILTERS = [
  ['all', 'All'], ['ready', 'Ready'], ['warned', 'Something to know'], ['failed', 'Failed'], ['writing', 'Writing'],
  ['sending', 'In sequence'], ['paused', 'Paused'], ['replied', 'Replied'], ['stopped', 'Stopped'], ['finished', 'Finished'],
];
const FILTER_COUNT = { all: 'total', ready: 'ready', warned: 'ready_warned', failed: 'failed', writing: 'writing', sending: 'in_sequence', paused: 'paused', replied: 'replied', stopped: 'stopped', finished: 'finished' };

// C1-SEQ7: the run's calendar booking link, shown and changed in the header.
function BookingLink({ run, authFetch, flash, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const r = await authFetch('/api/sequences/runs/' + run.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ booking_url: edit.trim() || null }) });
      setEdit(null);
      flash(r.without_link ? 'Saved. Drafts written before there was a link do not have it: rewrite them to add it.'
        : r.updated ? 'Saved. ' + plural(r.updated, 'person', 'people') + '\u2019s unsent emails now use it.' : 'Saved.');
      onSaved();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  if (edit !== null) {
    return (
      <span className="gtsq-bookedit">
        <input autoFocus type="url" value={edit} maxLength={500} placeholder="https://calendly.com/your-name/20min" onChange={(e) => setEdit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }} />
        <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
        <button type="button" className="gtc-primary" disabled={busy} onClick={save}>Save</button>
      </span>
    );
  }
  return run.booking_url
    ? <span>Booking link <a href={run.booking_url} target="_blank" rel="noreferrer">{run.booking_url.replace(/^https:\/\//, '').slice(0, 40)}</a> <button type="button" className="gtsf-linkbtn" onClick={() => setEdit(run.booking_url)}>Change</button></span>
    : <span>No booking link <button type="button" className="gtsf-linkbtn" onClick={() => setEdit('')}>Add one</button></span>;
}

// C1-SEQ8: the LinkedIn batch for LinkedHelper, new people since the last
// download or everyone, with how to use it.
function LinkedInBatch({ runId, li, getToken, flash, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  async function download(which) {
    setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(API_BASE + '/api/sequences/runs/' + runId + '/linkedin.csv?which=' + which, { headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'The download failed.'); }
      const name = ((res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/) || [])[1] || 'linkedhelper.csv';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setOpen(false); onDone();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  return (
    <div className="gtsq-li-batch">
      <button type="button" className="gtc-btn-ghost" onClick={() => setOpen((o) => !o)}>LinkedIn batch ({li.ready})</button>
      {open && (
        <div className="gtsq-li-pop">
          <div className="gtsq-li-pophead">For LinkedHelper</div>
          <p>One row per approved person with a LinkedIn address who has not replied or been stopped (people with no email address are included). {li.ready ? (li.last_download ? 'Last downloaded ' + when(li.last_download) + '.' : 'Not downloaded yet.') : 'Nobody yet: approve people who have a LinkedIn address and they appear here.'}</p>{/* C1-SEQ12 */}
          <div className="gtsq-li-popacts">
            <button type="button" className="gtc-primary" disabled={busy || !li.new_since} onClick={() => download('new')}>New since last time ({li.new_since})</button>
            <button type="button" className="gtc-btn-ghost" disabled={busy || !li.ready} onClick={() => download('all')}>Everyone ({li.ready})</button>
          </div>
          <ol className="gtsq-li-how">{/* C1-SEQ13 */}
            <li>Campaign, Queue, Add, Upload Profiles URLs: choose this file. This adds the people.</li>
            <li>CRM, Custom variables: upload the same file again and tick the <code>cs_</code> columns. This adds their messages. Without it the messages are empty.</li>
            <li>In the message steps, write <code>{'{cs_dm1}'}</code> and <code>{'{cs_dm2}'}</code>. Invite with no note.</li>
          </ol>
        </div>
      )}
    </div>
  );
}

function RunView({ runId, authFetch, flash, navigate, getToken }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [people, setPeople] = useState(null);
  const [matching, setMatching] = useState(0);
  const [selId, setSelId] = useState(null);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const loaded = useRef(0);

  const loadRun = useCallback(async () => {
    try { setData(await authFetch('/api/sequences/runs/' + runId)); } catch (e) { flash(e.message, true); setData((d) => d || { error: e.message }); }
  }, [authFetch, flash, runId]);
  const loadPeople = useCallback(async (more) => {
    const offset = more ? loaded.current : 0;
    try {
      const r = await authFetch('/api/sequences/runs/' + runId + '/people?filter=' + filter + '&q=' + encodeURIComponent(q.trim()) + '&limit=200&offset=' + offset);
      setPeople((p) => (more ? (p || []).concat(r.people) : r.people));
      loaded.current = offset + r.people.length;
      setMatching(r.matching);
    } catch (e) { flash(e.message, true); if (!more) setPeople([]); }
  }, [authFetch, flash, runId, filter, q]);

  useEffect(() => { loadRun(); }, [loadRun, reload]);
  useEffect(() => { const t = setTimeout(() => loadPeople(false), q ? 250 : 0); return () => clearTimeout(t); }, [loadPeople, reload, q]);
  useEffect(() => { if (people && people.length && !people.some((p) => p.id === selId)) setSelId(people[0].id); }, [people]); // eslint-disable-line react-hooks/exhaustive-deps

  // While writing or sending, keep the numbers moving.
  // C1-SEQ6: also while anyone is being written (a rewrite), whatever the run's status says.
  const writingNow = !!(data && data.counts && data.counts.writing > 0);
  const live = writingNow || !!(data && data.run && (data.run.status === 'writing' || data.run.status === 'sending'));
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => { loadRun(); if (loaded.current <= 200) loadPeople(false); }, writingNow ? 3000 : 15000);
    return () => clearInterval(t);
  }, [live, writingNow, loadRun, loadPeople]);

  const refresh = () => setReload((x) => x + 1);
  const tickedIds = Object.keys(ticked).filter((k) => ticked[k]).map(Number);
  const tickedRows = (people || []).filter((p) => ticked[p.id]);

  async function approve(ids, all) {
    const run = data.run; const c = data.counts;
    const count = all ? c.ready : ids.length;
    const warned = all ? c.ready_warned : (people || []).filter((p) => ids.indexOf(p.id) !== -1 && ((p.warnings || []).length || (p.checks || []).length)).length;
    const ok = await gtConfirm({
      title: 'Approve ' + plural(count, 'person', 'people'),
      message: [
        'Email 1 goes to each of them in the next sending window (' + windowText(run.send_window) + '), then the follow-ups in the same thread unless they reply.',
        warned ? plural(warned, 'of them has', 'of them have') + ' something to know. Approving sends to them anyway.' : 'None of them has anything flagged.',
      ],
      confirmLabel: 'Approve',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await authFetch('/api/sequences/runs/' + runId + '/approve', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(all ? { all_ready: true } : { person_ids: ids }) });
      flash(plural(r.approved, 'person', 'people') + ' approved. ' + (r.window_open ? 'Email 1 goes within a minute or two.' : 'Email 1 goes ' + when(r.next_opening) + '.'));
      setTicked({}); refresh();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  async function act(action, ids, confirmText) {
    if (confirmText && !(await gtConfirm({ title: confirmText[0], message: confirmText[1], confirmLabel: confirmText[2], tone: confirmText[3] }))) return;
    setBusy(true);
    try {
      const r = await authFetch('/api/sequences/people/action', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, person_ids: ids }) });
      flash(r.changed ? plural(r.changed, 'person', 'people') + ' updated.' : 'Nothing to change for them.');
      setTicked({}); refresh();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  // C1-SEQ15: rewrite the ticked people. Anyone approved for LinkedIn only goes
  // back to waiting for approval, and into the next LinkedIn batch.
  async function rewriteMany(rows) {
    const li = rows.filter(wasApproved).length; // C1-SEQ16: anyone already approved
    const ok = await gtConfirm({
      title: 'Rewrite ' + plural(rows.length, 'person', 'people'),
      message: ['Their emails and LinkedIn messages are written again from scratch.',
        li ? plural(li, 'of them was', 'of them were') + ' already approved, so nothing goes to them until you approve again. Download the LinkedIn batch again after.' : 'Approve them when they are ready.'],
      confirmLabel: 'Rewrite',
    });
    if (!ok) return;
    setBusy(true);
    let done = 0; let err = null;
    for (const p of rows) {
      try { await authFetch('/api/sequences/people/' + p.id + '/rewrite', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); done += 1; }
      catch (e) { err = e.message; }
    }
    flash(err ? plural(done, 'person', 'people') + ' rewriting. ' + err : 'Rewriting ' + plural(done, 'person', 'people') + '. They appear here in a few seconds.', !!err);
    setTicked({}); refresh();
    setBusy(false);
  }
  async function runAction(path, confirmText, done) {
    if (!(await gtConfirm({ title: confirmText[0], message: confirmText[1], confirmLabel: confirmText[2], tone: confirmText[3] === 'primary' ? undefined : 'danger' }))) return; // C1-SEQ17
    setBusy(true);
    try { const r = await authFetch('/api/sequences/runs/' + runId + '/' + path, { method: 'POST' }); flash(done(r)); refresh(); }
    catch (e) { flash(e.message, true); }
    setBusy(false);
  }

  if (!data) return <div className="gtc-wrap"><GtLoader compact /></div>;
  if (data.error) return <div className="gtc-wrap"><div className="gtnt-banner gtnt-banner--rose">{data.error}</div></div>;
  const { run, counts: c } = data;
  const writingDone = c.total - c.writing;
  const stoppedRun = run.status === 'stopped';

  return (
    <div className="gtc-wrap gtsq gtsq-runview">
      <button type="button" className="gtsq-back" onClick={() => navigate('/sequences')}>‹ All sequences</button>
      <div className="gtsq-head">
        <div className="gtsq-head-main">
          <div className="gtsq-head-title">
            <h1 className="gtc-title">{run.role_title}</h1>
            <Pill status={run.status} map={RUN_STATUS} />
          </div>
          <div className="gtsq-head-sub">
            <span>{(run.plan && run.plan.name) || run.name}: {planSummary(run.plan)}</span>
            <span className="gtsq-from">Sending from <b>{run.sender_email || 'the default address'}</b>{run.sender_name ? ' (' + run.sender_name + ')' : ''}</span>{/* C1-SEQ12 */}
            <span>Sends {windowText(run.send_window)}</span>
            <span>{run.disclose_client ? 'Company named' : 'Company not named'}</span>
            {run.source_label && <span>From {run.source_label}</span>}
            <BookingLink run={run} authFetch={authFetch} flash={flash} onSaved={refresh} />{/* C1-SEQ7 */}
            <button type="button" className="gtsf-linkbtn" onClick={() => navigate('/roles/' + (run.role_ref || run.role_id))}>Open the role</button>
          </div>
        </div>
        <div className="gtsq-head-acts">
          {c.writing > 0 && !stoppedRun && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => runAction('stop-writing', ['Stop writing', ['The ' + plural(c.writing, 'person', 'people') + ' not written yet are left out.', 'Everything already written stays, ready to approve.'], 'Stop writing'], (r) => plural(r.stopped, 'person', 'people') + ' left out.')}>Stop writing</button>}
          {!stoppedRun && run.status !== 'finished' && <button type="button" className="gtc-btn-ghost gtr-danger" disabled={busy} onClick={() => runAction('stop', ['Stop this sequence', ['Nobody in it gets another email.', 'Emails already sent stay sent. You can reopen it later for anyone not yet emailed.'], 'Stop everyone'], (r) => plural(r.stopped, 'person', 'people') + ' stopped.')}>Stop sequence</button>}
          {data.linkedin && run.plan && run.plan.linkedin_on && <LinkedInBatch runId={runId} li={data.linkedin} getToken={getToken} flash={flash} onDone={refresh} />}{/* C1-SEQ8 */}
          {stoppedRun && <button type="button" className="gtc-primary" disabled={busy} onClick={() => runAction('reopen', ['Reopen this sequence', ['Everyone it stopped who had not been emailed goes back to waiting for approval.', 'You can rewrite, edit and approve them again. Nothing goes until you approve.'], 'Reopen', 'primary'], (r) => plural(r.reopened, 'person', 'people') + ' back to waiting for approval.')}>Reopen sequence</button>}{/* C1-SEQ17 */}
          {c.ready > 0 && !stoppedRun && <button type="button" className="gtc-primary" disabled={busy} onClick={() => approve([], true)}>Approve all ready ({c.ready})</button>}
        </div>
      </div>

      {(data.notes || []).map((n, i) => <div key={i} className="gtnt-banner gtnt-banner--amber">{n}</div>)}
      {c.writing > 0 && !stoppedRun && (
        <div className="gtsq-writing">
          <div className="gtsf-bar"><i style={{ width: pct(writingDone, c.total) + '%' }} /></div>
          <span>Writing {writingDone} of {c.total}. You can approve people as they appear, or leave this page: it carries on.</span>
        </div>
      )}
      {run.status === 'sending' && !data.window_open && data.next_opening && (
        <div className="gtnt-banner gtnt-banner--slate">Outside the sending window. The next emails go {when(data.next_opening)}.</div>
      )}

      <div className="gtsf-tiles gtsq-tiles">
        <div className={'gtsf-tile' + (c.ready ? ' gtsf-tile--warn' : '')}><b>{c.ready}</b><span>ready to approve</span></div>
        <div className="gtsf-tile gtsf-tile--go"><b>{c.in_sequence + c.paused}</b><span>in sequence</span></div>
        <div className="gtsf-tile"><b>{c.reached}</b><span>reached</span></div>
        <div className="gtsf-tile gtsf-tile--go"><b>{c.replied}</b><span>replied{c.reached ? ', ' + pct(c.replied, c.reached) + '%' : ''}</span></div>
        <div className="gtsf-tile"><b>{c.stopped}</b><span>stopped</span></div>
        <div className="gtsf-tile"><b>{c.finished}</b><span>finished, no reply</span></div>
      </div>
      {(data.replied_by_step || []).length > 0 && (
        <div className="gtsq-bystep">Replies came after {data.replied_by_step.map((s) => 'email ' + s.step + ': ' + s.n).join(' · ')}</div>
      )}

      <div className="gtsq-panes">
        <div className="gtsq-rail">
          <input className="gtem-psearch" type="search" placeholder={'Find someone in these ' + c.total} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="gtsq-filters">
            {FILTERS.filter(([k]) => k === 'all' || c[FILTER_COUNT[k]] > 0 || filter === k).map(([k, l]) => (
              <button type="button" key={k} className={'gtsb-chip' + (filter === k ? ' gtsb-chip--on' : '')} onClick={() => { setFilter(k); setTicked({}); }}>
                {l} <b>{c[FILTER_COUNT[k]] || 0}</b>
              </button>
            ))}
          </div>
          {tickedIds.length > 0 && (
            <div className="gtsq-bulk">
              <b>{tickedIds.length} ticked</b>
              {tickedRows.some((p) => p.status === 'ready') && <button type="button" className="gtc-primary" disabled={busy} onClick={() => approve(tickedRows.filter((p) => p.status === 'ready').map((p) => p.id))}>Approve {tickedRows.filter((p) => p.status === 'ready').length}</button>}
              {tickedRows.some((p) => p.status === 'approved' || p.status === 'sending') && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => act('pause', tickedIds)}>Pause</button>}
              {tickedRows.some((p) => p.status === 'paused') && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => act('resume', tickedIds)}>Resume</button>}
              {!stoppedRun && tickedRows.some(canRewrite) && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => rewriteMany(tickedRows.filter(canRewrite))}>Rewrite {tickedRows.filter(canRewrite).length}</button>}{/* C1-SEQ15 */}
              <button type="button" className="gtc-btn-ghost gtr-danger" disabled={busy} onClick={() => act('stop', tickedIds, ['Stop ' + plural(tickedIds.length, 'person', 'people'), ['They get no more emails from this sequence.'], 'Stop', 'danger'])}>Stop</button>
              <button type="button" className="gtsf-linkbtn" onClick={() => setTicked({})}>Clear</button>
            </div>
          )}
          <div className="gtsq-people">
            {people === null ? <GtLoader compact /> : people.length === 0 ? <div className="gtsf-muted gtsq-none">Nobody here.</div> : (
              <>
                <label className="gtsq-tickall">
                  <input type="checkbox" checked={people.length > 0 && people.every((p) => ticked[p.id])}
                    onChange={(e) => { const t = {}; if (e.target.checked) people.forEach((p) => { t[p.id] = true; }); setTicked(t); }} />
                  <span>Tick all {people.length} shown</span>
                </label>
                {people.map((p) => {
                  const flags = (p.warnings || []).length + (p.checks || []).length;
                  return (
                    <div key={p.id} className={'gtsq-prow' + (selId === p.id ? ' gtsq-prow--on' : '')} onClick={() => setSelId(p.id)}>
                      <input type="checkbox" aria-label={'Tick ' + p.name} checked={!!ticked[p.id]} onClick={(e) => e.stopPropagation()} onChange={(e) => setTicked((t) => ({ ...t, [p.id]: e.target.checked }))} />
                      <div className="gtsq-prow-main">
                        <div className="gtsq-prow-name"><b>{p.name || p.ref}</b>{flags > 0 && ['ready', 'failed'].indexOf(p.status) !== -1 && <span className="gtsq-flag" title="Something to know">{flags}</span>}</div>
                        <div className="gtsq-prow-sub">{[p.current_title, p.current_company].filter(Boolean).join(' · ') || p.email || '—'}</div>
                        <div className="gtsq-prow-status"><Pill status={p.status} label={personLabel(p)} /></div>
                      </div>
                    </div>
                  );
                })}
                {matching > people.length && <button type="button" className="gtem-seeall" onClick={() => loadPeople(true)}>Show {Math.min(200, matching - people.length)} more of {matching}</button>}
              </>
            )}
          </div>
        </div>
        <div className="gtsq-detail">
          {selId ? <PersonPane key={selId} pid={selId} version={reload} authFetch={authFetch} flash={flash} navigate={navigate} busyAll={busy}
            onApprove={(id) => approve([id])} onAct={act} onChanged={refresh} runStopped={stoppedRun} />
            : <div className="gtsq-pick">Pick someone on the left to read their sequence.</div>}
        </div>
      </div>
    </div>
  );
}

// ── One person's sequence ─────────────────────────────────────────────────
const EDITABLE = ['ready', 'failed', 'approved', 'sending', 'paused'];
// C1-SEQ15: no email address, so LinkedIn only. Still editable and rewritable.
const liOnly = (p) => p.status === 'stopped' && p.stop_reason === 'No email address';
// C1-SEQ16: anyone not emailed yet, including people approved whose email 1 has not gone.
const wasApproved = (p) => liOnly(p) || (['approved', 'paused'].indexOf(p.status) !== -1 && !p.current_step);
const canRewrite = (p) => ['ready', 'failed'].indexOf(p.status) !== -1 || wasApproved(p);
function PersonPane({ pid, version, authFetch, flash, navigate, busyAll, onApprove, onAct, onChanged, runStopped }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null); // { step, subject, body }
  const [liEdit, setLiEdit] = useState(null);
  const [rewrite, setRewrite] = useState(null);
  const [busy, setBusy] = useState(false);
  // C1-SEQ6: when this person's status changes (written, approved, sent), the
  // list and the counts are refreshed too.
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const lastStatus = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/sequences/people/' + pid);
      setD(r);
      const was = lastStatus.current;
      lastStatus.current = r.person.status;
      if (was && was !== r.person.status && changedRef.current) changedRef.current();
    } catch (e) { flash(e.message, true); }
  }, [authFetch, flash, pid]);
  useEffect(() => { load(); }, [load, version]);
  // While being written, look again shortly.
  useEffect(() => {
    if (!d || ['queued', 'writing'].indexOf(d.person.status) === -1) return undefined;
    const t = setTimeout(load, 3000); return () => clearTimeout(t);
  }, [d, load]);
  const msgs = useMemo(() => {
    const m = {}; (d ? d.messages : []).forEach((x) => { m[x.seq_step] = x; }); return m;
  }, [d]);
  if (!d) return <GtLoader compact />;
  const p = d.person;
  const steps = Array.isArray(p.steps) ? p.steps : [];
  const canEdit = (EDITABLE.indexOf(p.status) !== -1 || liOnly(p)) && !runStopped; // C1-SEQ15
  const subject = steps[0] ? steps[0].subject || '' : '';

  async function save(body) {
    setBusy(true);
    try {
      const r = await authFetch('/api/sequences/people/' + pid, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      setD((x) => ({ ...x, person: { ...x.person, ...r.person } }));
      setEdit(null); setLiEdit(null); flash('Saved.'); onChanged();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  async function doRewrite() {
    setBusy(true);
    try {
      await authFetch('/api/sequences/people/' + pid + '/rewrite', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: rewrite || '' }) });
      setRewrite(null); flash(wasApproved(p) ? 'Rewriting. Approve it again when it is ready' + (p.linkedin ? ', and download the LinkedIn batch again.' : '.') : 'Rewriting. It appears here in a few seconds.'); // C1-SEQ16
      await load(); onChanged(); // C1-SEQ15
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  }
  function stepState(s, i) {
    const m = msgs[i + 1];
    if (s.skipped) return ['Skipped', 'slate'];
    if (m) {
      if (m.status === 'sent' || m.status === 'delivered') return ['Sent ' + when(m.sent_at), 'green'];
      if (m.status === 'queued' || m.status === 'sending') return ['Going now', 'teal'];
      if (m.status === 'bounced') return ['Bounced', 'rose'];
      if (m.status === 'failed') return ['Failed: ' + (m.error_message || ''), 'rose'];
      return [m.status, 'slate'];
    }
    if (['approved', 'sending'].indexOf(p.status) !== -1) {
      if (i === p.current_step && p.next_send_at) return ['Next, from ' + when(p.next_send_at), 'teal'];
      return ['Day ' + s.day + ', if no reply', 'slate'];
    }
    if (['ready', 'failed', 'queued', 'writing'].indexOf(p.status) !== -1) return [i === 0 ? 'Goes first, once approved' : 'Day ' + s.day + ', if no reply', 'slate'];
    return ['Not sent', 'slate'];
  }
  const li = p.linkedin;
  const live = ['approved', 'sending'].indexOf(p.status) !== -1;
  const n = steps.length;

  return (
    <div className="gtsq-person">
      <div className="gtsq-person-head">
        <div>
          <div className="gtsq-person-name">
            <button type="button" className="gtsq-namebtn" onClick={() => navigate('/candidates/' + (p.ref || p.candidate_id))}>{p.name || p.ref}</button>
            <Pill status={p.status} label={personLabel({ ...p, step_count: n })} />
          </div>
          <div className="gtsq-person-sub">
            {[p.current_title, p.current_company].filter(Boolean).join(' · ')}
            {p.email ? <span>{p.email}</span> : <span className="gtsq-warntext">No email address</span>}
            {linkedinHref(p.linkedin_url) ? <a href={linkedinHref(p.linkedin_url)} target="_blank" rel="noreferrer">LinkedIn ↗</a>
              : p.linkedin_url ? <span className="gtsq-warntext" title={'Saved as "' + p.linkedin_url + '"'}>LinkedIn field is not a link</span> : null}{/* C1-SEQ6 */}
          </div>
        </div>
        <div className="gtsq-person-acts">
          {p.status === 'ready' && !runStopped && <button type="button" className="gtc-primary" disabled={busy || busyAll} onClick={() => onApprove(p.id)}>Approve</button>}
          {canRewrite(p) && !runStopped && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => setRewrite(rewrite === null ? '' : null)}>Rewrite</button>}{/* C1-SEQ15 */}
          {live && <button type="button" className="gtc-btn-ghost" disabled={busyAll} onClick={() => onAct('send_now', [p.id], ['Send the next email now', ['It goes within a minute, outside the sending window if need be, without waiting the planned days.'], 'Send now'])}>Send next now</button>}
          {(live || p.status === 'paused') && p.current_step < n && <button type="button" className="gtc-btn-ghost" disabled={busyAll} onClick={() => onAct('skip', [p.id])}>Skip next email</button>}
          {live && <button type="button" className="gtc-btn-ghost" disabled={busyAll} onClick={() => onAct('pause', [p.id])}>Pause</button>}
          {p.status === 'paused' && <button type="button" className="gtc-btn-ghost" disabled={busyAll} onClick={() => onAct('resume', [p.id])}>Resume</button>}
          {EDITABLE.indexOf(p.status) !== -1 && <button type="button" className="gtc-btn-ghost gtr-danger" disabled={busyAll} onClick={() => onAct('stop', [p.id], ['Stop ' + (p.name || 'this person'), ['They get no more emails from this sequence.'], 'Stop', 'danger'])}>Stop</button>}
        </div>
      </div>

      {rewrite !== null && (
        <div className="gtsq-rewrite">
          <input autoFocus value={rewrite} maxLength={800} placeholder="What should change? For example: shorter, and lead with their open source work." onChange={(e) => setRewrite(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doRewrite(); if (e.key === 'Escape') setRewrite(null); }} />
          <button type="button" className="gtc-btn-ghost" onClick={() => setRewrite(null)}>Cancel</button>
          <button type="button" className="gtc-primary" disabled={busy} onClick={doRewrite}>Rewrite</button>
        </div>
      )}
      {p.status === 'failed' && p.error && <div className="gtnt-banner gtnt-banner--rose">Writing failed: {p.error}. Rewrite to try again.</div>}
      {['queued', 'writing'].indexOf(p.status) !== -1 && <div className="gtnt-banner gtnt-banner--slate">Being written. It appears here in a few seconds.</div>}
      {p.stop_reason && ['stopped', 'bounced', 'unsubscribed'].indexOf(p.status) !== -1 && <div className="gtnt-banner gtnt-banner--slate">Stopped: {p.stop_reason}{p.stopped_at ? ', ' + when(p.stopped_at) : ''}.</div>}
      {p.status === 'replied' && <div className="gtnt-banner gtnt-banner--green">Replied {when(p.replied_at)}{p.replied_after_step ? ', after email ' + p.replied_after_step : ''}. Nothing more goes. The reply is on their Emails tab.</div>}
      {p.approved_at && <div className="gtsq-approved">Approved by {p.approved_by_name || 'GTCO'}, {when(p.approved_at)}</div>}

      {((p.warnings || []).length > 0 || (p.checks || []).length > 0) && (
        <div className="gtsq-flags">
          {(p.warnings || []).map((w) => <span key={'w' + w.key} className={'gtem-warn gtem-warn--' + w.key}>{w.text}</span>)}
          {(p.checks || []).map((w, i) => <span key={'c' + i} className="gtem-warn gtsq-check-flag">{w.text}</span>)}
        </div>
      )}

      {steps.map((s, i) => {
        const st = stepState(s, i);
        const sentOrGoing = !!msgs[i + 1] || i < p.current_step;
        const editing = edit && edit.step === i + 1;
        return (
          <div key={i} className={'gtsq-step' + (s.skipped ? ' gtsq-step--skipped' : '')}>
            <div className="gtsq-step-head">
              <b>Email {i + 1}</b>
              <span className={'gtsq-pill gtsq-pill--' + st[1]}>{st[0]}</span>
              {s.edited && <span className="gtsq-edited">edited</span>}
              <div className="gtbn-spacer" />
              {canEdit && !sentOrGoing && !editing && <button type="button" className="gtsf-linkbtn" onClick={() => setEdit({ step: i + 1, subject, body: s.body_html || '' })}>Edit</button>}
            </div>
            {editing ? (
              <div className="gtsq-step-edit">
                {i === 0 && <label className="gtc-field"><span>Subject</span><input value={edit.subject} maxLength={300} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} /></label>}
                <div className="gtsf-editor"><GtEditor value={edit.body} onChange={(v) => setEdit((x) => ({ ...x, body: v }))} minHeight={200} /></div>
                <div className="gtsq-step-editacts">
                  <button type="button" className="gtc-btn-ghost" onClick={() => setEdit(null)}>Cancel</button>
                  <button type="button" className="gtc-primary" disabled={busy} onClick={() => save({ steps: [i === 0 ? { step: 1, subject: edit.subject, body_html: edit.body } : { step: i + 1, body_html: edit.body }] })}>Save</button>
                </div>
              </div>
            ) : (
              <>
                <div className="gtsq-subject"><span>Subject</span>{i === 0 ? subject : 'Re: ' + subject}</div>
                <div className="gtsq-body" dangerouslySetInnerHTML={{ __html: s.body_html || '' }} />
              </>
            )}
          </div>
        );
      })}
      {steps.length > 0 && <div className="gtsf-hint">Your signature and the unsubscribe line are added to each email when it goes. Follow-ups go in the same thread.</div>}

      {li && (
        <div className="gtsq-step gtsq-li">
          <div className="gtsq-step-head">
            <b>LinkedIn, for LinkedHelper</b>
            {li.edited && <span className="gtsq-edited">edited</span>}
            <div className="gtbn-spacer" />
            {canEdit && !liEdit && <button type="button" className="gtsf-linkbtn" onClick={() => setLiEdit({ note: li.connection_note || '', hasNote: !!li.connection_note, messages: (li.messages || []).slice() })}>Edit</button>}
          </div>
          {liEdit ? (
            <div className="gtsq-step-edit">
              {liEdit.hasNote && <label className="gtc-field"><span>Connection note <em className={'gtbn-opt' + (liEdit.note.length > 300 ? ' gtsq-over' : '')}>{liEdit.note.length} of 300</em></span>
                <textarea rows={3} value={liEdit.note} onChange={(e) => setLiEdit({ ...liEdit, note: e.target.value })} /></label>}{/* C1-SEQ7: only on older drafts */}
              {liEdit.messages.map((m, k) => (
                <label key={k} className="gtc-field"><span>Message {k + 1}</span>
                  <textarea rows={5} value={m} onChange={(e) => { const ms = liEdit.messages.slice(); ms[k] = e.target.value; setLiEdit({ ...liEdit, messages: ms }); }} /></label>
              ))}
              <div className="gtsq-step-editacts">
                <button type="button" className="gtc-btn-ghost" onClick={() => setLiEdit(null)}>Cancel</button>
                <button type="button" className="gtc-primary" disabled={busy} onClick={() => save({ linkedin: liEdit.hasNote ? { connection_note: liEdit.note, messages: liEdit.messages } : { messages: liEdit.messages } })}>Save</button>
              </div>
            </div>
          ) : (
            <>
              {li.connection_note && <div className="gtsq-li-part"><span>Connection note · {(li.connection_note || '').length} of 300</span><p>{li.connection_note}</p></div>}
              {(li.messages || []).map((m, k) => <div key={k} className="gtsq-li-part"><span>Message {k + 1}</span><p>{m}</p></div>)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
